import { useState, useEffect, useCallback } from "react";

const BASE_URL = "https://jwa0g1z9d6.execute-api.eu-north-1.amazonaws.com";
const ANTHROPIC_URL = `${BASE_URL}/api/claude`;

const TOPICS = [
  { id: "markets", label: "MARKETS", icon: "▲", query: "stock market S&P 500 equities today" },
  { id: "macro", label: "MACRO", icon: "◈", query: "macroeconomics Fed interest rates inflation GDP today" },
  { id: "energy", label: "ENERGY & COMMODITIES", icon: "◆", query: "oil gas commodities gold prices today" },
];

const formatTime = () => {
  const now = new Date();
  return now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
};

const formatDate = () => {
  return new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};

function TopicPanel({ topic, isActive, onClick, loading, theme, isMobile }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: isActive ? theme.accent : "transparent",
        border: isActive ? "none" : `1px solid ${theme.borderSecondary}`,
        color: isActive ? theme.accentText : theme.textMuted,
        padding: isMobile ? "10px 12px" : "8px 16px",
        fontSize: isMobile ? "11px" : "12px",
        fontFamily: "'DM Mono', monospace",
        fontWeight: "600",
        letterSpacing: "0.12em",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        transition: "all 0.15s ease",
        flex: isMobile ? "1" : "unset",
      }}
    >
      <span style={{ fontSize: "8px" }}>{topic.icon}</span>
      {topic.label}
      {loading && isActive && <span style={{ animation: "pulse 1s infinite", fontSize: "8px" }}>●</span>}
    </button>
  );
}

function NewsCard({ item, index, theme, isMobile }) {
  return (
    <div
      style={{
        borderBottom: `1px solid ${theme.border}`,
        padding: isMobile ? "12px 0" : "14px 0",
        animation: `fadeIn 0.3s ease ${index * 0.05}s both`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "6px", flexWrap: "wrap" }}>
            <span style={{
              fontSize: "9px",
              fontFamily: "'DM Mono', monospace",
              color: theme.accent,
              letterSpacing: "0.1em",
              fontWeight: "600",
            }}>
              {item.source}
            </span>
            <span style={{ color: theme.borderSecondary, fontSize: "11px" }}>·</span>
            <span style={{ fontSize: "9px", fontFamily: "'DM Mono', monospace", color: theme.textFaint }}>
              {item.time}
            </span>
            {item.sentiment && (
              <>
                <span style={{ color: theme.borderSecondary, fontSize: "9px" }}>·</span>
                <span style={{
                  fontSize: "9px",
                  fontFamily: "'DM Mono', monospace",
                  color: item.sentiment === "bullish" ? theme.green : item.sentiment === "bearish" ? theme.red : theme.textMuted,
                  fontWeight: "700",
                  letterSpacing: "0.08em",
                }}>
                  {item.sentiment === "bullish" ? "▲ BULL" : item.sentiment === "bearish" ? "▼ BEAR" : "— NEUTRAL"}
                </span>
              </>
            )}
          </div>
          <p style={{
            margin: "0 0 6px 0",
            fontSize: isMobile ? "14px" : "15px",
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: "600",
            color: theme.text,
            lineHeight: "1.3",
            letterSpacing: "0.01em",
          }}>
            {item.headline}
          </p>
          <p style={{
            margin: 0,
            fontSize: isMobile ? "12px" : "13px",
            fontFamily: "'DM Mono', monospace",
            color: theme.textMuted,
            lineHeight: "1.6",
          }}>
            {item.summary}
          </p>
        </div>
        {item.value && !isMobile && (
          <div style={{
            textAlign: "right",
            flexShrink: 0,
            background: theme.bgSecondary,
            padding: "8px 12px",
            border: `1px solid ${theme.border}`,
          }}>
            <div style={{
              fontSize: "15px",
              fontFamily: "'DM Mono', monospace",
              fontWeight: "700",
              color: item.change?.startsWith("+") ? theme.green : item.change?.startsWith("-") ? theme.red : theme.text,
            }}>
              {item.value}
            </div>
            {item.change && (
              <div style={{
                fontSize: "10px",
                fontFamily: "'DM Mono', monospace",
                color: item.change.startsWith("+") ? theme.green : theme.red,
              }}>
                {item.change}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Show value inline on mobile */}
      {item.value && isMobile && (
        <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "8px" }}>
          <span style={{
            fontSize: "12px",
            fontFamily: "'DM Mono', monospace",
            fontWeight: "700",
            color: item.change?.startsWith("+") ? theme.green : item.change?.startsWith("-") ? theme.red : theme.text,
          }}>
            {item.value}
          </span>
          {item.change && (
            <span style={{
              fontSize: "10px",
              fontFamily: "'DM Mono', monospace",
              color: item.change.startsWith("+") ? theme.green : theme.red,
            }}>
              {item.change}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryBar({ summary, loading, theme, isMobile }) {
  return (
    <div style={{
      background: theme.bgCard,
      border: `1px solid ${theme.border}`,
      borderLeft: `3px solid ${theme.accent}`,
      padding: isMobile ? "10px 12px" : "12px 16px",
      marginBottom: "20px",
    }}>
      <div style={{
        fontSize: "9px",
        fontFamily: "'DM Mono', monospace",
        color: theme.accent,
        letterSpacing: "0.15em",
        marginBottom: "6px",
        fontWeight: "700",
      }}>
        AI BRIEF
      </div>
      <p style={{
        margin: 0,
        fontSize: isMobile ? "12px" : "13px",
        fontFamily: "'DM Mono', monospace",
        color: loading ? theme.borderSecondary : theme.textMuted,
        lineHeight: "1.7",
      }}>
        {loading ? "Fetching latest SUMMIT RIDGE INVESTMENTSs..." : summary}
      </p>
    </div>
  );
}

export default function FinancialDashboard() {
  const [activeTab, setActiveTab] = useState("markets");
  const [time, setTime] = useState(formatTime());
  const [topicData, setTopicData] = useState({});
  const [loading, setLoading] = useState({});
  const [ticker, setTicker] = useState([]);
  const [darkMode, setDarkMode] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const theme = {
    bg: darkMode ? "#080808" : "#f5f5f5",
    bgSecondary: darkMode ? "#0a0a0a" : "#ffffff",
    bgCard: darkMode ? "#0f0f0f" : "#ffffff",
    border: darkMode ? "#1a1a1a" : "#e0e0e0",
    borderSecondary: darkMode ? "#2a2a2a" : "#d0d0d0",
    text: darkMode ? "#e8e8e8" : "#1a1a1a",
    textMuted: darkMode ? "#888" : "#555",
    textFaint: darkMode ? "#444" : "#999",
    accent: "#e8f400",
    accentText: "#0a0a0a",
    green: "#4ade80",
    red: "#f87171",
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTime(formatTime()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const fetchTicker = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/ticker`);
        const data = await res.json();
        if (Array.isArray(data)) setTicker(data);
      } catch (err) {
        console.error("Ticker fetch failed:", err);
      }
    };
    fetchTicker();
    const interval = setInterval(fetchTicker, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchTopicData = useCallback(async (topicId) => {
    if (topicData[topicId]) return;
    const topic = TOPICS.find(t => t.id === topicId);
    setLoading(prev => ({ ...prev, [topicId]: true }));

    try {
      const marketRes = await fetch(`${BASE_URL}/api/market-data?topic=${topicId}`);
      const marketData = await marketRes.json();

      const response = await fetch(ANTHROPIC_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5",
          max_tokens: 1500,
          system: `You are a financial news formatter. Given real news headlines and price data, return ONLY a valid JSON object with this exact shape:
{
  "brief": "2-3 sentence market brief",
  "items": [
    {
      "headline": "short punchy headline",
      "summary": "1-2 sentence insight",
      "source": "SOURCE NAME",
      "time": "Xh ago",
      "sentiment": "bullish|bearish|neutral",
      "value": "optional price",
      "change": "optional like +1.2%"
    }
  ]
}
Return 4-5 items. Do NOT include any citation tags, markdown, HTML, or <cite> tags. Plain text only inside JSON strings. Only return the JSON object, no preamble.`,
          messages: [{
            role: "user",
            content: `Format this data for the ${topic.label} dashboard. Return only raw JSON, no citation tags or markup:

Headlines: ${JSON.stringify(marketData.headlines)}
Quotes: ${JSON.stringify(marketData.quotes)}`
          }],
        }),
      });

      const data = await response.json();
      const text = data.content
        .filter(b => b.type === "text")
        .map(b => b.text)
        .join("");

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      const parsed = JSON.parse(jsonMatch ? jsonMatch[0] : text);
      setTopicData(prev => ({ ...prev, [topicId]: parsed }));
    } catch (err) {
      console.error("Error:", err);
      setTopicData(prev => ({
        ...prev,
        [topicId]: {
          brief: "Unable to fetch live data. Please try refreshing.",
          items: [],
        },
      }));
    } finally {
      setLoading(prev => ({ ...prev, [topicId]: false }));
    }
  }, [topicData]);

  useEffect(() => {
    fetchTopicData(activeTab);
  }, [activeTab]);

  const current = topicData[activeTab];
  const isLoading = loading[activeTab];

  return (
    <div style={{
      minHeight: "100vh",
      background: theme.bg,
      color: theme.text,
      fontFamily: "'DM Mono', monospace",
      padding: "0",
      transition: "background 0.2s ease, color 0.2s ease",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&family=Barlow+Condensed:wght@400;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #2a2a2a; }
      `}</style>

      {/* Header */}
      <div style={{
        borderBottom: `1px solid ${theme.border}`,
        padding: isMobile ? "12px 16px" : "16px 28px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "sticky",
        top: 0,
        background: theme.bg,
        zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
          <span style={{
            fontSize: isMobile ? "14px" : "18px",
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: "800",
            letterSpacing: "0.15em",
            color: theme.accent,
          }}>SUMMIT RIDGE INVESTMENTS</span>
          {!isMobile && (
            <span style={{ fontSize: "9px", color: theme.accent, letterSpacing: "0.12em" }}>
              DAILY FINANCIAL INTELLIGENCE
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "10px" : "20px" }}>
          <button
            onClick={() => setDarkMode(!darkMode)}
            style={{
              background: "transparent",
              border: `1px solid ${theme.borderSecondary}`,
              color: theme.textMuted,
              padding: isMobile ? "4px 8px" : "6px 12px",
              fontSize: "9px",
              fontFamily: "'DM Mono', monospace",
              letterSpacing: "0.1em",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            {darkMode ? "☀" : "☾"}
          </button>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: isMobile ? "12px" : "16px", fontWeight: "500", letterSpacing: "0.05em", color: theme.text }}>{time}</div>
            {!isMobile && (
              <div style={{ fontSize: "9px", color: theme.textFaint, letterSpacing: "0.08em" }}>{formatDate()}</div>
            )}
          </div>
        </div>
      </div>

      {/* Ticker strip */}
      <div style={{
  borderBottom: `1px solid ${theme.border}`,
  background: theme.bgSecondary,
  padding: isMobile ? "6px 0" : "7px 0",
  overflow: "hidden",
  fontSize: isMobile ? "11px" : "13px",
  letterSpacing: "0.08em",
}}>
  <div style={{
    display: "flex",
    gap: isMobile ? "16px" : "24px",
    animation: "ticker 30s linear infinite",
    width: "max-content",
  }}>
    {/* Render ticker twice for seamless loop */}
    {[...ticker, ...ticker].map(({ label, value, change, up }, i) => (
      <div key={i} style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0, padding: "0 12px" }}>
        <span style={{ color: theme.textMuted }}>{label}</span>
        <span style={{ color: theme.text, fontWeight: "500" }}>{value}</span>
        <span style={{ color: up ? theme.green : theme.red }}>{up ? "+" : ""}{change}%</span>
      </div>
    ))}
  </div>
</div>

      <div style={{ padding: isMobile ? "12px 16px" : "20px 28px", maxWidth: "900px", margin: "0 auto" }}>

        {/* Tab bar */}
        <div style={{
          display: "flex",
          gap: "6px",
          marginBottom: "16px",
          flexWrap: isMobile ? "nowrap" : "wrap",
        }}>
          {TOPICS.map(topic => (
            <TopicPanel
              key={topic.id}
              topic={topic}
              isActive={activeTab === topic.id}
              onClick={() => setActiveTab(topic.id)}
              loading={isLoading}
              theme={theme}
              isMobile={isMobile}
            />
          ))}
          {!isMobile && (
            <button
              onClick={() => {
                setTopicData({});
                setTimeout(() => fetchTopicData(activeTab), 50);
              }}
              style={{
                marginLeft: "auto",
                background: "transparent",
                border: `1px solid ${theme.borderSecondary}`,
                color: theme.textMuted,
                padding: "8px 14px",
                fontSize: "9px",
                fontFamily: "'DM Mono', monospace",
                letterSpacing: "0.1em",
                cursor: "pointer",
              }}
            >
              ↻ REFRESH
            </button>
          )}
        </div>

        {/* Mobile refresh button */}
        {isMobile && (
          <button
            onClick={() => {
              setTopicData({});
              setTimeout(() => fetchTopicData(activeTab), 50);
            }}
            style={{
              width: "100%",
              background: "transparent",
              border: `1px solid ${theme.borderSecondary}`,
              color: theme.textMuted,
              padding: "8px",
              fontSize: "9px",
              fontFamily: "'DM Mono', monospace",
              letterSpacing: "0.1em",
              cursor: "pointer",
              marginBottom: "16px",
            }}
          >
            ↻ REFRESH
          </button>
        )}

        {/* Summary bar */}
        <SummaryBar summary={current?.brief} loading={isLoading && !current} theme={theme} isMobile={isMobile} />

        {/* News items */}
        {isLoading && !current ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: theme.textFaint, fontSize: "11px", letterSpacing: "0.1em" }}>
            LOADING LIVE DATA...
          </div>
        ) : (
          <div>
            {(current?.items || []).map((item, i) => (
              <NewsCard key={i} item={item} index={i} theme={theme} isMobile={isMobile} />
            ))}
          </div>
        )}

        <div style={{
          marginTop: "32px",
          paddingTop: "12px",
          borderTop: `1px solid ${theme.border}`,
          fontSize: "11px",
          color: theme.textFaint,
          letterSpacing: "0.08em",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          gap: isMobile ? "4px" : "0",
        }}>
          <span>SUMMIT RIDGE INVESTMENTS · FINANCIAL INTELLIGENCE</span>
          <span>DATA MAY BE DELAYED · NOT FINANCIAL ADVICE</span>
        </div>
      </div>
    </div>
  );
}
